import { Component, OnInit, Inject } from '@angular/core';
import { filter } from 'rxjs/operators';

import { CalendarService } from './calendar.service';
import { DATEPICKER_INPUT_DIRECTIVE } from '../datepicker-input/datepicker-input.token';
import { DatepickerInputDirective } from '../datepicker-input/datepicker-input.directive';
import { DatepickerValidatorService } from '../datepicker-validator.service';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'cf-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss']
})
export class CalendarComponent implements OnInit {
  months: string[];
  days: number[][];
  years: number[];
  weekdays: string[];

  maxMonth: number;
  maxDay: number;
  minDay: number;
  maxYear: number;

  // state
  currentMonth: number;
  currentDay: number;
  currentYear: number;

  // next/previous buttons
  previousDisabled: boolean;
  nextDisabled: boolean;

  constructor(
    private calendarSvc: CalendarService,
    private datepickerValidatorSvc: DatepickerValidatorService,
    @Inject(DATEPICKER_INPUT_DIRECTIVE) private datepicker: DatepickerInputDirective
  ) { }

  highlightTodaysDay(day) {
    if (
      this.currentMonth === this.calendarSvc.getCurrentMonth() &&
      day === +this.calendarSvc.getCurrentDay() &&
      this.currentYear === this.calendarSvc.getCurrentYear()) {
      return true;
    } else {
      return false;
    }
  }

  getDaysOfMonth(month, year) {
    this.currentMonth = +month;
    this.currentYear = +year; // needs to be a number
    this.days = this.calendarSvc.getDaysOfMonth(this.currentMonth, this.currentYear);

    // only limit the days dropdown if we are on max month and max year
    // have to add one because datepicker is a JS date and JS month count starts at 0
    // where moment.js month count starts at 1
    if (this.datepickerValidatorSvc.maxDate.getFullYear() === this.currentYear) {
      if (this.datepickerValidatorSvc.maxDate.getMonth() === this.currentMonth) {
        this.maxDay = this.datepickerValidatorSvc.maxDate.getDate();
      } else if (this.datepickerValidatorSvc.maxDate.getMonth() < this.currentMonth) {
        this.maxDay = 0;
      } else {
        this.maxDay = 31;
      }
    } else {
      this.maxDay = 31;
    }

    // if current year is less than min date
    if (this.currentYear < this.datepickerValidatorSvc.minDate.getFullYear()) {
      this.minDay = 32; // hide all days

    // current year greater then min year show all days
    } else if (this.currentYear > this.datepickerValidatorSvc.minDate.getFullYear()) {
      this.minDay = 1;

    // else...
    } else {
      // if min month is equal to current month need to get actual min day
      if (this.datepickerValidatorSvc.minDate.getMonth() === this.currentMonth) {
        this.minDay = this.datepickerValidatorSvc.minDate.getDate();

      // but if current month is less than min month hide all days
      } else if (this.currentMonth < this.datepickerValidatorSvc.minDate.getMonth()) {
        this.minDay = 32;

      // else current month greater than min month show all days
      } else {
        this.minDay = 1;
      }
    }

    // if there is no day selected then don't update the input value
    if ( this.currentDay ) {
      this.setDatepickerInput(month, this.currentDay, year, false);
    }

    this.setNextPrevousButtons();
  }

  setDay(day) {
    if (day > 0) {
      this.currentDay = day;
      this.setDatepickerInput(this.currentMonth, day, this.currentYear, true);
    }
  }

  previousMonth() {
    if (this.previousDisabled) {
      return;
    }

    if (this.currentMonth - 1 === -1) {
      this.getDaysOfMonth(11, this.currentYear - 1);
    } else {
      this.getDaysOfMonth(this.currentMonth - 1, this.currentYear);
    }
  }

  nextMonth() {
    if (this.nextDisabled) {
      return;
    }

    if (this.currentMonth + 1 === 12) {
      this.getDaysOfMonth(0, this.currentYear + 1);
    } else {
      this.getDaysOfMonth(this.currentMonth + 1, this.currentYear);
    }
  }

  ngOnInit() {
    // get values
    this.months = this.calendarSvc.getMonths(12);
    this.years = this.calendarSvc.getYears(this.datepickerValidatorSvc.maxDate, this.datepickerValidatorSvc.minDate);
    this.weekdays = this.calendarSvc.getWeekdays();

    // if input has a valid date already then set
    // if datepicker has any errors we just show the default date
    if (
      this.datepickerValidatorSvc.isValid(this.datepicker.element.nativeElement.value)
      && this.isValidYear(this.datepicker.element.nativeElement.value)
      && this.datepicker.element.nativeElement.value !== ''
    ) {
      this.setCurrentDate(this.calendarSvc.getDate(this.datepicker.element.nativeElement.value));
    } else {
      // set default state
      this.setDefaultState();
    }

    // subscribe to input value changes
    this.datepicker.inputValueChange.pipe(
      filter((value: string) => value.length >= 10)
    ).subscribe(value => {
      if (this.isValidDate(value) && this.isValidYear(value)) {
        const date = this.calendarSvc.getDate(value);
        this.setCurrentDate(date);
      } else {
        this.setDefaultState();
      }
    });
  }

  private setNextPrevousButtons() {
    if (this.currentMonth === 11 && this.currentYear === this.datepickerValidatorSvc.maxDate.getFullYear()) {
      this.nextDisabled = true;
    } else {
      this.nextDisabled = false;
    }

    // if current month is january and year is min year then button should be disabled
    if (this.currentMonth === 0 && this.currentYear === this.datepickerValidatorSvc.minDate.getFullYear()) {
      this.previousDisabled = true;
    } else {
      this.previousDisabled = false;
    }
  }

  private isValidDate(value) {
    return this.datepickerValidatorSvc.isValid(value);
  }

  private setDatepickerInput(month, day, year, dayChange) {
    const date = (
      this.calendarSvc.getMonthNumber(month) + '/' +
      this.calendarSvc.getDayNumber(day)  + '/' +
      this.currentYear
    );

    // only set the date if it is unique
    if (date !== this.datepicker.element.nativeElement.value) {
      this.datepicker.setValue({
        value: date,
        dayChange
      });

    // we still want to close the datepicker
    // even if day changes
    } else if (dayChange) {
      this.datepicker.setValue({
        value: date,
        dayChange
      });
    }
  }

  private setCurrentDate(date) {
    this.currentMonth = date.month;
    this.currentDay = date.day;
    this.currentYear = date.year;
    this.getDaysOfMonth(date.month, date.year);
  }

  private setDefaultState() {
    this.currentDay = null;
    this.getDaysOfMonth(this.calendarSvc.getCurrentMonth(), this.calendarSvc.getCurrentYear());
  }

  private isValidYear(value) {
    const year = this.calendarSvc.getDate(value).year;

    if (year < this.datepickerValidatorSvc.minDate.getFullYear() || year > this.datepickerValidatorSvc.maxDate.getFullYear()) {
      return false;
    }

    return true;
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/datepicker/calendar/calendar.component.ts